import React, { type ReactNode } from 'react';
import { getWidgetData } from 'src/api/get-widget-data';
import { adaptWebResponse } from 'src/api/widget-data-contract';
import { MOBILE_BRIDGE_QUERY_PARAM_NAME } from 'src/constants/common';
import { mobileBridge } from 'src/services/mobile-bridge';
import { type WebRequest, type WebResponse } from 'src/types/widget-data';
import { dataDog } from 'src/utils/datadog/data-dog';

export interface WidgetDataRuntime {
  load: (request: WebRequest) => Promise<WebResponse>;
}

export const loadWidgetDataFromProductionSources = async (
  request: WebRequest
): Promise<WebResponse> => {
  const shouldGetDataFromMobileBridge =
    typeof window !== 'undefined' &&
    window.location.search.includes(MOBILE_BRIDGE_QUERY_PARAM_NAME);

  if (shouldGetDataFromMobileBridge) {
    try {
      return adaptWebResponse(await mobileBridge.awaitData());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('mobileBridge.awaitData error', error);
      dataDog.error('mobileBridge.awaitData error', error);
    }
  }

  return getWidgetData(request);
};

const productionWidgetDataRuntime: WidgetDataRuntime = {
  load: loadWidgetDataFromProductionSources,
};

const WidgetDataRuntimeContext = React.createContext<WidgetDataRuntime>(
  productionWidgetDataRuntime
);

export const WidgetDataRuntimeProvider = ({
  children,
  runtime,
}: {
  children: ReactNode;
  runtime: WidgetDataRuntime;
}) => (
  <WidgetDataRuntimeContext.Provider value={runtime}>
    {children}
  </WidgetDataRuntimeContext.Provider>
);

export const useWidgetDataRuntime = () =>
  React.useContext(WidgetDataRuntimeContext);
